// UNIVERSAL-CONTENT-DISPLAY — the pure content-document contract.
//
// ONE derivation rule for "what format is this stored body" and ONE safe-HTML
// string form, replacing the per-surface `bodyHtml || body→<br/>` copies and
// their double sanitizing. This file is deliberately React-free and
// `.ts`-only so the check:richedit-roundtrip harness can execute it directly.
//
// The stored bytes are never modified: emptiness checks trim a COPY, the
// original value flows through. Sanitizing/escaping happens exactly once, at
// the display boundary (here or in ContentView) — consumers must not
// pre-sanitize.

import { sanitizeRichContentHtml } from '../serialize/richHtml'
import { detectDataDocumentKind, type KnownDataKind } from '../serialize/dataEnvelope'
import { parseContactsDocument, renderContactsHtml } from '../serialize/contacts'
import { parseLockedNotice, renderLockedNoticeHtml } from '../serialize/lockedNotice'
import { isCustomHtmlDocument, renderCustomHtmlFrame } from '../serialize/customHtml'

// 'locked' is the one SYNTHETIC kind: never stored, built by surfaces from the
// access grant (serialize/lockedNotice.ts) — resolveContentDocument never
// produces it, so stored bytes cannot impersonate the locked screen.
export type ContentKind = 'plain' | 'html' | 'code' | 'locked' | KnownDataKind

export interface ContentDocument {
  kind: ContentKind
  value: string
}

export interface ContentSourceFields {
  bodyHtml?: string | null
  body?: string | null
}

/** The one format-selection rule: stored HTML wins; otherwise plain text;
 * otherwise no content (surfaces own their empty/protected visuals). A stored
 * body that is a whole-document rich_data envelope of a KNOWN kind resolves
 * to that kind; unknown envelope kinds stay on the html path and render
 * their machine-generated digest. */
export function resolveContentDocument(input: ContentSourceFields): ContentDocument | null {
  const html = typeof input.bodyHtml === 'string' && input.bodyHtml.trim() ? input.bodyHtml : ''
  if (html) return { kind: detectDataDocumentKind(html) ?? 'html', value: html }
  const text = typeof input.body === 'string' && input.body.trim() ? input.body : ''
  if (text) return { kind: 'plain', value: text }
  return null
}

export function escapeContentText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * The string form of a rendered document, for consumers that need safe HTML
 * outside React (the site `main.iframe` template contract). Mirrors the
 * ContentView renderers: html is sanitized, everything else is escaped.
 * An unregistered kind falls back to escaped plain text — never raw bytes.
 */
export function renderContentToSafeHtml(doc: ContentDocument): string {
  if (doc.kind === 'custom-html' || (doc.kind === 'html' && isCustomHtmlDocument(doc.value))) return renderCustomHtmlFrame(doc.value)
  if (doc.kind === 'html') return sanitizeRichContentHtml(doc.value)
  if (doc.kind === 'code') return `<pre><code>${escapeContentText(doc.value)}</code></pre>`
  if (doc.kind === 'contacts') {
    const parsed = parseContactsDocument(doc.value)
    // A malformed envelope degrades to its sanitized digest, never raw bytes.
    return parsed ? renderContactsHtml(parsed) : sanitizeRichContentHtml(doc.value)
  }
  if (doc.kind === 'locked') {
    const parsed = parseLockedNotice(doc.value)
    // Synthetic JSON, not stored markup: malformed renders nothing.
    return parsed ? renderLockedNoticeHtml(parsed) : ''
  }
  return escapeContentText(doc.value).replace(/(?:\r\n|\r|\n)/g, '<br />')
}

/** Custom-element markers that require the client enhancer (kept in step
 * with elements/registry.ts). */
export const RICH_ELEMENT_MARKER_RE = /<(?:user-profile|ui-accordion|accordion-section|message-callout|progress-meter|content-tabs|content-tab|current-time)(?:\s|>)/

// ---------------------------------------------------------------------------
// Content frames — presentation backdrops around rendered content.
//
// Styles are package-owned inline values driven by CSS custom properties
// (the `--rich-*` token pattern the custom elements already use): surfaces
// re-theme through the vars without touching the package, and the defaults
// work standalone (no Tailwind discovery involved). Frame specs come from
// SURFACE code, not from stored user data.

export interface ContentFrameSpec {
  kind: string
  /** Any CSS background value; the solid/page default is white. */
  background?: string
}

export type ContentFrameStyle = Record<string, string>
export type ContentFrameBuilder = (spec: ContentFrameSpec) => ContentFrameStyle | null

function solidFrame(spec: ContentFrameSpec): ContentFrameStyle {
  return {
    background: 'var(--content-frame-bg, #ffffff)',
    ...(spec.background ? { '--content-frame-bg': spec.background } : {}),
  }
}

const frameBuilders: Record<string, ContentFrameBuilder> = {
  none: () => null,
  solid: solidFrame,
  page: (spec) => ({
    ...solidFrame(spec),
    padding: 'var(--content-frame-padding, 24px 28px)',
    borderRadius: 'var(--content-frame-radius, 3px)',
    border: 'var(--content-frame-border, 1px solid rgba(15, 23, 32, 0.08))',
    boxShadow: 'var(--content-frame-shadow, 0 1px 2px rgba(15, 23, 32, 0.10), 0 10px 28px rgba(15, 23, 32, 0.12))',
  }),
}

/** Register a new frame look. One entry — no consumer changes required. */
export function registerContentFrame(kind: string, builder: ContentFrameBuilder): void {
  frameBuilders[kind] = builder
}

/** Style for a frame spec; null means "render bare" (none/unknown kinds). */
export function contentFrameStyle(spec: ContentFrameSpec | undefined): ContentFrameStyle | null {
  if (!spec) return null
  const builder = frameBuilders[spec.kind]
  return builder ? builder(spec) : null
}
